import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API, SLOT_LABELS, formatApiError, serviceTitle } from "@/lib/content";
import { ServiceChips, Field, inputCls } from "./ServiceChips";
import { PromoEstimate } from "./PromoEstimate";

const EMPTY = { name: "", email: "", phone: "", address: "", notes: "" };

const SlotPicker = ({ date, value, onChange, refreshKey }) => {
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    if (!date) return undefined;
    let alive = true;
    axios
      .get(`${API}/slots`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => { if (alive) setSlots(Array.isArray(r.data?.slots) ? r.data.slots : []); })
      .catch(() => { if (alive) setSlots([]); });
    return () => { alive = false; };
  }, [date, refreshKey]);
  if (!date) return <p className="text-sm text-slate-400">Pick a date to see open times.</p>;
  if (!slots.length) return <p className="text-sm text-slate-400">Loading times…</p>;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="booking-slots">
      {slots.map((s) => (
        <button type="button" key={s.time} disabled={!s.available} onClick={() => onChange(s.time)} data-testid={`booking-slot-${s.time.replace(":", "")}`}
          className={`rounded-xl border px-3 py-3 text-sm transition-colors ${!s.available ? "cursor-not-allowed border-white/5 text-slate-600 line-through" : value === s.time ? "border-amber-300 bg-amber-400 font-semibold text-[#0B0F17]" : "border-white/15 text-slate-200 hover:border-amber-300"}`}>
          {SLOT_LABELS[s.time] ?? s.time}
        </button>
      ))}
    </div>
  );
};

const Done = ({ b, onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-10 text-center" data-testid="booking-success">
    <CheckCircle2 className="mx-auto h-14 w-14 text-amber-300" />
    <h3 className="mt-5 font-display text-3xl font-bold text-white">You're booked, {b.name.split(" ")[0]}.</h3>
    <p className="mx-auto mt-3 max-w-md text-slate-300">
      {b.services.map(serviceTitle).join(", ")} on {format(new Date(`${b.date}T00:00:00`), "EEEE, MMM d")} at {SLOT_LABELS[b.time]}.{b.promo_code ? ` Code ${b.promo_code} applied.` : ""} We'll contact you to confirm.
    </p>
    <button onClick={onReset} data-testid="booking-new-btn" className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:border-amber-300">Book another</button>
  </motion.div>
);

export const BookingForm = ({ services, setServices, promo, setPromo }) => {
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!services.length) return toast.error("Choose at least one service");
    if (!date || !time) return toast.error("Pick a date and a time slot");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...f, services, promo_code: promo?.code ?? "", date: format(date, "yyyy-MM-dd"), time });
      setDone(data);
      toast.success("Booking request received");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setTime("");
      setRefreshKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Done b={done} onReset={() => { setDone(null); setF(EMPTY); setTime(""); setDate(undefined); }} />;

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
      <Field label="1 · Services"><ServiceChips value={services} onChange={setServices} prefix="booking" /></Field>
      <div className="grid gap-8 lg:grid-cols-2">
        <Field label="2 · Date">
          <div className="w-fit rounded-2xl border border-white/10 bg-white/[0.03] p-2" data-testid="booking-calendar">
            <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setTime(""); }} disabled={{ before: addDays(new Date(), 1) }} />
          </div>
        </Field>
        <Field label="3 · Time"><SlotPicker date={date} value={time} onChange={setTime} refreshKey={refreshKey} /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" /></Field>
        <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" /></Field>
        <Field label="Phone"><input required value={f.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" /></Field>
        <Field label="Address"><input required value={f.address} onChange={set("address")} className={inputCls} data-testid="booking-address-input" /></Field>
      </div>
      <Field label="Notes (optional)"><textarea rows={3} value={f.notes} onChange={set("notes")} className={`${inputCls} h-auto py-3`} placeholder="Pets, access codes, focus areas…" data-testid="booking-notes-input" /></Field>
      <PromoEstimate services={services} promo={promo} setPromo={setPromo} prefix="booking" />
      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} disabled={busy} type="submit" data-testid="booking-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-4 font-semibold text-[#0B0F17] transition-colors hover:bg-amber-300 disabled:opacity-60 sm:w-auto">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm booking request
      </motion.button>
    </form>
  );
};
